import React, { useState, useEffect } from 'react';
import {
  Grid,
  TextField,
  Typography,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert
} from '@mui/material';
import { LocationOn as LocationIcon } from '@mui/icons-material';

function OrganizationalAssignmentSection({ 
  location, 
  isEditing, 
  onDataChange, 
  disabled,
  orgUnits = [],
  hierarchyDefinition = []
}) {
  // Store original value to detect real changes
  const originalOrgLevel1 = location?.orgLevel_1 || '';
  
  const [formData, setFormData] = useState({
    orgLevel_1: originalOrgLevel1,
    changeReason: ''
  });

  const [derivedOrgUnits, setDerivedOrgUnits] = useState({
    orgLevel_2: null,
    orgLevel_3: null
  });

  // Get active ORGLEVEL_1 units for dropdown
  // Note: The field is called 'level' in the master list, not 'orgLevel'
  const availableOrgLevel1Units = orgUnits.filter(unit => 
    (unit.level === 'ORGLEVEL_1' || unit.orgLevel === 'ORGLEVEL_1') && unit.isActive !== false
  );

  // Get labels from hierarchy definition
  const getHierarchyLabel = (levelId) => {
    const level = hierarchyDefinition.find(h => h.levelId === levelId && h.isActive);
    return level?.label || levelId.replace('ORGLEVEL_', 'Level ');
  };

  // Initialize form data and derived org units when location changes or edit mode changes
  useEffect(() => {
    if (location) {
      setFormData({
        orgLevel_1: location.orgLevel_1 || '',
        changeReason: ''
      });
      
      // Initialize derived org units with current location's org assignments
      if (location.orgLevel_2 || location.orgLevel_3) {
        const level2Unit = location.orgLevel_2 ? 
          orgUnits.find(u => u.id === location.orgLevel_2) || 
          { id: location.orgLevel_2, name: location.orgLevel_2_name } : null;
        
        const level3Unit = location.orgLevel_3 ? 
          orgUnits.find(u => u.id === location.orgLevel_3) || 
          { id: location.orgLevel_3, name: location.orgLevel_3_name } : null;
        
        setDerivedOrgUnits({
          orgLevel_2: level2Unit,
          orgLevel_3: level3Unit
        });
      } else if (location.orgLevel_1 && orgUnits.length > 0) {
        // If only orgLevel_1 is set, derive the parent units
        const selectedUnit = orgUnits.find(u => u.id === location.orgLevel_1);
        if (selectedUnit) {
          // Note: parentId is used in master list, not parentOrgUnitId
          const level2Unit = (selectedUnit.parentId || selectedUnit.parentOrgUnitId) ? 
            orgUnits.find(u => u.id === (selectedUnit.parentId || selectedUnit.parentOrgUnitId)) : null;
          
          const level3Unit = level2Unit && (level2Unit.parentId || level2Unit.parentOrgUnitId) ? 
            orgUnits.find(u => u.id === (level2Unit.parentId || level2Unit.parentOrgUnitId)) : null;
          
          setDerivedOrgUnits({
            orgLevel_2: level2Unit,
            orgLevel_3: level3Unit
          });
        }
      }
    }
  }, [location, isEditing, orgUnits, availableOrgLevel1Units.length]); // Added availableOrgLevel1Units.length dependency

  // When orgLevel_1 changes by user action, derive orgLevel_2 and orgLevel_3
  useEffect(() => {
    // Skip if this is the initial value from location
    if (formData.orgLevel_1 === location?.orgLevel_1) {
      return;
    }
    
    if (formData.orgLevel_1 && orgUnits.length > 0) {
      // Find the selected ORGLEVEL_1 unit
      const selectedUnit = orgUnits.find(u => u.id === formData.orgLevel_1);
      
      if (selectedUnit) {
        // Get parent units - use parentId (from master list) or parentOrgUnitId (from full data)
        const level2Unit = (selectedUnit.parentId || selectedUnit.parentOrgUnitId) ? 
          orgUnits.find(u => u.id === (selectedUnit.parentId || selectedUnit.parentOrgUnitId)) : null;
        
        const level3Unit = level2Unit && (level2Unit.parentId || level2Unit.parentOrgUnitId) ? 
          orgUnits.find(u => u.id === (level2Unit.parentId || level2Unit.parentOrgUnitId)) : null;
        
        setDerivedOrgUnits({
          orgLevel_2: level2Unit,
          orgLevel_3: level3Unit
        });
      }
    } else if (!formData.orgLevel_1) {
      setDerivedOrgUnits({
        orgLevel_2: null,
        orgLevel_3: null
      });
    }
  }, [formData.orgLevel_1, orgUnits, location]);

  const handleChange = (field, value) => {
    const newFormData = {
      ...formData,
      [field]: value
    };
    
    setFormData(newFormData);
    
    // Check if there's an actual change from original
    const hasChanges = newFormData.orgLevel_1 !== originalOrgLevel1;
    
    // Clear change reason if we're back to original
    if (!hasChanges && field === 'orgLevel_1') {
      newFormData.changeReason = '';
    }
    
    // Notify parent of changes
    if (onDataChange) {
      if (hasChanges && newFormData.changeReason?.trim()) {
        const selectedUnit = orgUnits.find(u => u.id === newFormData.orgLevel_1);
        
        onDataChange({
          orgLevel_1: newFormData.orgLevel_1,
          orgLevel_1_name: selectedUnit?.name || '',
          orgLevel_2: derivedOrgUnits.orgLevel_2?.id || null,
          orgLevel_2_name: derivedOrgUnits.orgLevel_2?.name || '',
          orgLevel_3: derivedOrgUnits.orgLevel_3?.id || null,
          orgLevel_3_name: derivedOrgUnits.orgLevel_3?.name || '',
          changeReason: newFormData.changeReason
        });
      } else if (hasChanges) {
        // Has changes but no reason - pass data but it won't enable save
        const selectedUnit = orgUnits.find(u => u.id === newFormData.orgLevel_1);
        onDataChange({
          orgLevel_1: newFormData.orgLevel_1,
          orgLevel_1_name: selectedUnit?.name || '',
          orgLevel_2: derivedOrgUnits.orgLevel_2?.id || null,
          orgLevel_2_name: derivedOrgUnits.orgLevel_2?.name || '',
          orgLevel_3: derivedOrgUnits.orgLevel_3?.id || null,
          orgLevel_3_name: derivedOrgUnits.orgLevel_3?.name || '',
          changeReason: newFormData.changeReason
        });
      } else {
        // Signal no changes by passing null
        onDataChange(null);
      }
    }
  };

  // Build current hierarchy display
  const buildHierarchyDisplay = () => {
    const items = [];
    
    // Add current assignments in hierarchical order
    if (location?.orgLevel_3_name || derivedOrgUnits.orgLevel_3) {
      items.push({
        level: getHierarchyLabel('ORGLEVEL_3'),
        name: derivedOrgUnits.orgLevel_3?.name || location.orgLevel_3_name
      });
    }
    
    if (location?.orgLevel_2_name || derivedOrgUnits.orgLevel_2) {
      items.push({
        level: getHierarchyLabel('ORGLEVEL_2'),
        name: derivedOrgUnits.orgLevel_2?.name || location.orgLevel_2_name
      });
    }
    
    if (location?.orgLevel_1_name || formData.orgLevel_1) {
      const selectedUnit = orgUnits.find(u => u.id === formData.orgLevel_1);
      items.push({
        level: getHierarchyLabel('ORGLEVEL_1'),
        name: selectedUnit?.name || location.orgLevel_1_name
      });
    }
    
    return items;
  };

  if (!isEditing) {
    const hierarchyItems = buildHierarchyDisplay();
    
    return (
      <Box>
        {hierarchyItems.length > 0 ? (
          <List dense>
            {hierarchyItems.map((item, index) => (
              <ListItem key={index}>
                <ListItemIcon><LocationIcon fontSize="small" /></ListItemIcon>
                <ListItemText 
                  primary={item.level} 
                  secondary={item.name}
                />
              </ListItem>
            ))}
          </List>
        ) : (
          <Alert severity="warning" variant="outlined">
            This location is not assigned to any organizational unit
          </Alert>
        )}
      </Box>
    );
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <FormControl fullWidth size="small" required>
          <InputLabel>{getHierarchyLabel('ORGLEVEL_1')} Assignment</InputLabel>
          <Select
            value={formData.orgLevel_1 || ''}
            onChange={(e) => handleChange('orgLevel_1', e.target.value)}
            disabled={disabled}
            label={`${getHierarchyLabel('ORGLEVEL_1')} Assignment`}
          >
            <MenuItem value="">
              <em>None (Unassigned)</em>
            </MenuItem>
            {availableOrgLevel1Units.length > 0 ? (
              availableOrgLevel1Units.map((unit) => (
                <MenuItem key={unit.id} value={unit.id}>
                  {unit.name}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>
                <em>No {getHierarchyLabel('ORGLEVEL_1')} units available</em>
              </MenuItem>
            )}
          </Select>
        </FormControl>
      </Grid>
      
      {/* Show derived parent units */}
      {(derivedOrgUnits.orgLevel_2 || location?.orgLevel_2_name) && (
        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            label={`${getHierarchyLabel('ORGLEVEL_2')} (Current)`}
            value={derivedOrgUnits.orgLevel_2?.name || location?.orgLevel_2_name || ''}
            disabled
            helperText={formData.orgLevel_1 !== originalOrgLevel1 ? 
              "Will be updated based on selected unit" : 
              "Current assignment"}
            InputProps={{
              style: { backgroundColor: '#f5f5f5' }
            }}
          />
        </Grid>
      )}
      
      {(derivedOrgUnits.orgLevel_3 || location?.orgLevel_3_name) && (
        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            label={`${getHierarchyLabel('ORGLEVEL_3')} (Current)`}
            value={derivedOrgUnits.orgLevel_3?.name || location?.orgLevel_3_name || ''}
            disabled
            helperText={formData.orgLevel_1 !== originalOrgLevel1 ? 
              "Will be updated based on selected unit" : 
              "Current assignment"}
            InputProps={{
              style: { backgroundColor: '#f5f5f5' }
            }}
          />
        </Grid>
      )}
      
      {/* Change reason is required for any changes */}
      {isEditing && (
        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            label="Change Reason"
            value={formData.changeReason}
            onChange={(e) => handleChange('changeReason', e.target.value)}
            disabled={disabled}
            required
            multiline
            rows={2}
            helperText="Please provide a reason for these changes"
          />
        </Grid>
      )}
      
      {/* Validation warnings */}
      {formData.orgLevel_1 && (
        <Grid item xs={12}>
          {(() => {
            const selectedUnit = orgUnits.find(u => u.id === formData.orgLevel_1);
            if (selectedUnit?.isActive === false) {
              return (
                <Alert severity="error" variant="outlined">
                  Warning: The selected unit is inactive. Please select an active unit.
                </Alert>
              );
            }
            return null;
          })()}
        </Grid>
      )}
    </Grid>
  );
}

export default OrganizationalAssignmentSection;